import { useEffect, useState } from 'react';

const CircleTimer = ({
  radius = 12,
  strokeWidth = 2,
  start,
  end,
}: {
  radius?: number;
  strokeWidth?: number;
  start: number;
  end: number;
}) => {
  const [offset, setOffset] = useState(0);

  const now = Date.now() / 1000;
  const totalDuration = end - start;
  const elapsedTime = now - start;
  const percentage = Math.min(100, (elapsedTime / totalDuration) * 100);

  const circumference = 2 * Math.PI * radius;
  const endingSoon = percentage > 75;
  const ended = percentage >= 100;

  // Adjust the offset to start fully green and turn transparent counter-clockwise
  useEffect(() => {
    const intervalId = setInterval(() => {
      // get the current progress and set that as a basis of the circumference and multiply by 1.<radius * 2> to handle
      // pixel sizing and rendering nuances
      setOffset((percentage / 100) * circumference * (1 + (radius * 2) / 100));
    }, 1000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [percentage, circumference, radius]);

  return (
    <span className="rounded-full bg-gray-50 flex items-center">
      {!ended && (
        <span className="block w-8 h-8 relative">
          <span className="absolute w-6 h-6 top-1 left-1">
            <svg
              width={radius * 2}
              height={radius * 2}
              viewBox={`0 0 ${radius * 2}px ${radius * 2}px`}
            >
              <circle
                className={`${
                  endingSoon ? 'text-red-500' : 'text-green-500'
                } stroke-current transition-colors`}
                fill="none"
                strokeWidth={strokeWidth}
                r={radius - strokeWidth / 2}
                cx={radius}
                cy={radius}
                strokeLinecap="round"
                transform={`rotate(-90 ${radius} ${radius})`} // Rotate by 90 degrees
                strokeDasharray={circumference}
                strokeDashoffset={offset}
              />
            </svg>
          </span>
          <span
            className={`transition-colors absolute w-3 h-3 top-2.5 left-2.5 ${
              endingSoon ? 'bg-red-500/25' : 'bg-green-500/25'
            } rounded-full`}
          />
          <span
            className={`transition-colors absolute w-2 h-2 top-3 left-3 ${
              endingSoon ? 'bg-red-500' : 'bg-green-500'
            } rounded-full`}
          />
        </span>
      )}
      {endingSoon && (
        <span
          className={`font-bold text-xs text-red-500 uppercase h-8 flex items-center ${
            ended ? 'px-4' : 'pr-2'
          }`}
        >
          {ended ? 'Ended' : 'Ending Soon'}
        </span>
      )}
    </span>
  );
};

export default CircleTimer;
